import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Building2, Users, Plus, Save, Settings, LayoutGrid, RefreshCw } from 'lucide-react';

const enconOrange = '#ee7203';

// Dashboard cards that can be toggled per branch — mirrors every card currently
// on the Home hub grid (routes/permission-gated add-ons included) so the admin
// checklist never goes stale when new cards are added.
const DASHBOARD_CARDS = [
  { id: 'sales_figures', name: 'Sales Figures', description: 'Sales performance data' },
  { id: 'sales_hub', name: 'Sales Hub', description: 'My performance + team call/order tracking' },
  { id: 'm4_uploads', name: 'M4 Uploads', description: 'M4 file management' },
  { id: 'calendar_uploads', name: 'Calendar Uploads', description: 'Annual leave calendar' },
  { id: 'free_stock_report', name: 'Free Stock Report', description: 'Stock availability' },
  { id: 'transport_data', name: 'Transport Data', description: 'Delivery logistics' },
  { id: 'stock_transfers', name: 'Stock Transfers', description: 'Inter-branch transfers' },
  { id: 'pro_formas', name: 'Pro Formas', description: 'Pro forma invoices' },
  { id: 'files', name: 'Files', description: 'File management' },
  { id: 'po_expenses', name: 'P.O. Expenses', description: 'Purchase order expenses' },
  { id: 'quotes', name: 'Quotes', description: 'Quote management' },
  { id: 'reports', name: 'Reports', description: 'Business reports' },
  { id: 'sales_dashboard', name: 'Sales Dashboard', description: 'Sales overview' },
  { id: 'pi_counts', name: 'PI Counts', description: 'Physical inventory counts (also controls the bundled Stock Management card below for users without the standalone toggle)' },
  { id: 'stock_management', name: 'Stock Management', description: 'Overstock & inventory management (needs the user permission too)' },
  { id: 'goods_in', name: 'Goods In', description: 'Goods receiving' },
  { id: 'wh_pi_counts', name: 'W/H PI Counts', description: 'Warehouse PI counts for verification' },
  { id: 'wh_goods_in', name: 'W/H Goods In', description: 'Warehouse goods receiving' },
  { id: 'customer_management', name: 'Customer Management', description: 'Customer database' },
  { id: 'error_log', name: 'Error Log', description: 'System error tracking' },
  { id: 'company_car', name: 'Company Car', description: 'Mileage tracking and vehicle management' },
  { id: 'staffology', name: 'Staffology', description: 'HR portal and employee management' },
  { id: 'crm', name: 'CRM', description: 'Zoho CRM quick-access tile' },
  { id: 'settings', name: 'Settings', description: 'App settings and preferences (always on for super admin)' },
];

const BranchManager = ({ user }) => {
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserAssignModal, setShowUserAssignModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [newBranchCode, setNewBranchCode] = useState('');
  const [newBranchName, setNewBranchName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dashboardCards, setDashboardCards] = useState({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadBranches();
    loadUsers();
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/branches`);
      setBranches(response.data.branches || []);
      if (response.data.branches?.length > 0 && !selectedBranch) {
        setSelectedBranch(response.data.branches[0]);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get(`${API}/users`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadDashboardSettings = async (branchId) => {
    try {
      const response = await api.get(`${API}/branches/${branchId}/dashboard-cards`);
      setDashboardCards(response.data.cards || {});
    } catch (error) {
      console.error('Error loading dashboard settings:', error);
      setDashboardCards({});
    }
  };

  const handleCreateBranch = async () => {
    if (!newBranchCode.trim() || !newBranchName.trim()) {
      toast.error('Please enter branch code and name');
      return;
    }

    setSaving(true);
    try {
      await api.post(`${API}/branches`, {
        code: newBranchCode.toUpperCase(),
        name: newBranchName
      });
      toast.success('Branch created successfully');
      setShowCreateModal(false);
      setNewBranchCode('');
      setNewBranchName('');
      loadBranches();
    } catch (error) {
      console.error('Error creating branch:', error);
      toast.error(error.response?.data?.detail || 'Failed to create branch');
    } finally {
      setSaving(false);
    }
  };

  // Limited Access UI: switch a whole branch on or off (optimistic, rolls back on failure).
  const handleToggleBranchLimited = async (branch, value) => {
    setBranches(prev => prev.map(b => (b.id === branch.id ? { ...b, limited_access: value } : b)));
    try {
      await api.put(`${API}/branches/${branch.id}/limited-access`, { limited_access: value });
      toast.success(`${branch.code}: limited access UI ${value ? 'on' : 'off'}`);
    } catch (error) {
      console.error('Error updating limited access:', error);
      setBranches(prev => prev.map(b => (b.id === branch.id ? { ...b, limited_access: !value } : b)));
      toast.error(error.response?.data?.detail || 'Failed to update limited access');
    }
  };

  // Create a branch partition for every depot in the branch directory that does not have one yet.
  const handleSyncFromDirectory = async () => {
    setSyncing(true);
    try {
      const response = await api.post(`${API}/branches/sync-from-directory`, {});
      const created = response.data?.created || [];
      toast.success(created.length
        ? `Set up ${created.length} branch${created.length === 1 ? '' : 'es'}: ${created.join(', ')}`
        : 'Every branch is already set up');
      await loadBranches();
    } catch (error) {
      console.error('Error setting up branches:', error);
      toast.error(error.response?.data?.detail || 'Failed to set up branches');
    } finally {
      setSyncing(false);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUserId || !selectedBranch) {
      toast.error('Please select a user');
      return;
    }

    setSaving(true);
    try {
      await api.post(`${API}/branches/${selectedBranch.id}/assign-user`, {
        user_id: selectedUserId
      });
      toast.success('User assigned to branch');
      setShowUserAssignModal(false);
      setSelectedUserId('');
      loadUsers();
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDashboardSettings = async () => {
    if (!selectedBranch) return;

    setSaving(true);
    try {
      // Ensure every known card gets an explicit true/false value before saving.
      // Without this, a newly-added card (e.g. Sales Hub) that was never touched
      // stays "undefined" in the stored settings — and the Home hub treats an
      // undefined internal-app key as HIDDEN once a branch has any explicit
      // settings saved, even though this checklist shows it as checked/visible.
      const completeCards = { ...dashboardCards };
      DASHBOARD_CARDS.forEach(card => {
        if (completeCards[card.id] === undefined) completeCards[card.id] = true;
      });

      await api.put(`${API}/branches/${selectedBranch.id}/dashboard-cards`, {
        cards: completeCards
      });
      setDashboardCards(completeCards);
      toast.success('Dashboard settings saved');
      setShowDashboardModal(false);
    } catch (error) {
      console.error('Error saving dashboard settings:', error);
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDashboardCard = (cardId) => {
    setDashboardCards(prev => {
      const currentlyVisible = prev[cardId] !== false;
      return { ...prev, [cardId]: !currentlyVisible };
    });
  };

  const getBranchUsers = (branchId) => {
    // Return users where primary branch_id matches OR branch is in their branch_ids array
    return users.filter(u => 
      u.branch_id === branchId || 
      (Array.isArray(u.branch_ids) && u.branch_ids.includes(branchId))
    );
  };

  // Check if a user has access to a specific branch
  const userHasBranchAccess = (userId, branchId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return false;
    return user.branch_id === branchId || 
      (Array.isArray(user.branch_ids) && user.branch_ids.includes(branchId));
  };

  // Toggle user's access to a branch
  const toggleUserBranchAccess = async (userId, branchId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    setSaving(true);
    try {
      const currentBranches = Array.isArray(user.branch_ids) ? [...user.branch_ids] : [];
      let newBranches;
      
      if (currentBranches.includes(branchId)) {
        // Remove branch access
        newBranches = currentBranches.filter(b => b !== branchId);
      } else {
        // Add branch access
        newBranches = [...currentBranches, branchId];
      }
      
      // Update user permissions
      await api.put(`${API}/users/${userId}/permissions`, {
        branch_ids: newBranches
      });
      
      toast.success(`Branch access ${newBranches.includes(branchId) ? 'granted' : 'revoked'}`);
      loadUsers();
    } catch (error) {
      console.error('Error toggling branch access:', error);
      toast.error('Failed to update branch access');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: enconOrange }} />
        <span className="ml-2">Loading branches...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
            <Building2 className="w-6 h-6" />
            Branch Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage branches and user assignments. Each branch has separate data for sales, transport, PI counts, etc.
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          style={{ backgroundColor: enconOrange }}
          className="text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Branch
        </Button>
      </div>

      {/* Limited Access UI - branch partitions */}
      <Card data-testid="limited-access-setup-card" className="border-orange-200 bg-orange-50/60">
        <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-semibold" style={{ color: enconOrange }}>Limited Access UI</h4>
            <p className="text-sm text-gray-600">
              Every depot needs its own branch here before its users can log in and be set up.
              New branches start with limited access on (C30 and E70 stay off). Construction Products
              sites and the stock-only sites B28 and B29 are skipped.
            </p>
          </div>
          <Button
            data-testid="sync-branches-from-directory-btn"
            variant="outline"
            onClick={handleSyncFromDirectory}
            disabled={syncing}
            className="shrink-0"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Setting up...' : 'Set Up Missing Branches'}
          </Button>
        </CardContent>
      </Card>

      {/* Branch Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {branches.map(branch => {
          const branchUsers = getBranchUsers(branch.id);
          const isSelected = selectedBranch?.id === branch.id;
          
          return (
            <Card 
              key={branch.id}
              className={`cursor-pointer transition-all ${
                isSelected ? 'ring-2 ring-orange-500 shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => setSelectedBranch(branch)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" style={{ color: enconOrange }} />
                    {branch.display_name}
                  </CardTitle>
                  {branch.is_default && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-semibold">
                      Default
                    </span>
                  )}
                </div>
                <CardDescription>
                  Code: {branch.code} • {branchUsers.length} users assigned
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBranch(branch);
                      setShowUserAssignModal(true);
                    }}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    Manage Users
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBranch(branch);
                      loadDashboardSettings(branch.id);
                      setShowDashboardModal(true);
                    }}
                  >
                    <LayoutGrid className="w-4 h-4 mr-1" />
                    Dashboard Cards
                  </Button>
                </div>
                {/* Limited Access UI toggle */}
                <div
                  className="mt-3 pt-3 border-t flex items-center justify-between"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs font-medium text-gray-600">Limited access UI</span>
                  <Switch
                    data-testid={`branch-limited-access-${branch.id}`}
                    checked={branch.limited_access === true}
                    onCheckedChange={(value) => handleToggleBranchLimited(branch, value)}
                  />
                </div>

                {/* Users preview */}
                {branchUsers.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-1">Users:</p>
                    <div className="flex flex-wrap gap-1">
                      {branchUsers.slice(0, 5).map(u => (
                        <span 
                          key={u.id} 
                          className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {u.full_name || u.username}
                        </span>
                      ))}
                      {branchUsers.length > 5 && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                          +{branchUsers.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <h4 className="font-semibold text-blue-800 mb-2">Branch Data Separation</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p><strong>Separate per branch:</strong> Sales figures, M4 uploads, Calendar/AL, Free stock report, Transport, Stock transfers, Pro formas, Files, P.O. expenses, Quotes, Reports, Sales Dashboard, PI Counts, Goods In</p>
            <p><strong>Shared globally:</strong> Master stock database (product codes), Customer database</p>
            <p className="mt-2 text-blue-600">Note: E70 starts fresh with no historical data. Stock is searchable but won't show free stock unless uploaded per branch.</p>
          </div>
        </CardContent>
      </Card>

      {/* Create Branch Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              <Plus className="w-5 h-5" />
              Create New Branch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Branch Code</label>
              <Input
                value={newBranchCode}
                onChange={(e) => setNewBranchCode(e.target.value.toUpperCase())}
                placeholder="e.g., E70"
                className="mt-1"
                maxLength={5}
              />
              <p className="text-xs text-gray-500 mt-1">Short unique identifier (e.g., C30, E70)</p>
            </div>
            <div>
              <label className="text-sm font-medium">Branch Name</label>
              <Input
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                placeholder="e.g., West Brom"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Full name of the branch location</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBranch} 
              disabled={saving}
              style={{ backgroundColor: enconOrange }}
              className="text-white"
            >
              {saving ? 'Creating...' : 'Create Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign User Modal */}
      <Dialog open={showUserAssignModal} onOpenChange={setShowUserAssignModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              <Users className="w-5 h-5" />
              Manage Users - {selectedBranch?.display_name}
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">
              Users can have access to multiple branches. Check the boxes below to grant/revoke access.
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* All users with branch access toggle */}
            <div>
              <h4 className="text-sm font-medium mb-2">User Branch Access</h4>
              <div className="max-h-96 overflow-y-auto space-y-2 border rounded-lg p-3">
                {users.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">No users available</p>
                ) : (
                  users
                    .map(u => {
                      const hasAccess = userHasBranchAccess(u.id, selectedBranch?.id);
                      const isPrimaryBranch = u.branch_id === selectedBranch?.id;
                      
                      return (
                        <div 
                          key={u.id} 
                          className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                            hasAccess ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{u.full_name || u.username}</span>
                              {isPrimaryBranch && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-orange-500 text-white rounded-full">
                                  Primary
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">{u.email}</span>
                            {u.branch_ids?.length > 0 && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                Access: {[u.branch_id, ...(u.branch_ids || [])].filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isPrimaryBranch ? (
                              <span className="text-xs text-gray-400 italic">Primary branch</span>
                            ) : (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={hasAccess}
                                  onChange={() => toggleUserBranchAccess(u.id, selectedBranch?.id)}
                                  disabled={saving}
                                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-gray-600">
                                  {hasAccess ? 'Has Access' : 'Grant Access'}
                                </span>
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Super admins automatically have access to all branches. Primary branch cannot be changed here - use "Assign User" to change primary branch.
              </p>
            </div>
            
            {/* Assign as primary branch */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Set Primary Branch</h4>
              <p className="text-xs text-gray-500 mb-2">
                Move a user's primary branch to {selectedBranch?.display_name}
              </p>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {users
                    .filter(u => u.branch_id !== selectedBranch?.id)
                    .map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name || u.username} - Currently: {u.branch_id || 'C30'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserAssignModal(false)}>
              Close
            </Button>
            <Button 
              onClick={handleAssignUser} 
              disabled={saving || !selectedUserId}
              style={{ backgroundColor: enconOrange }}
              className="text-white"
            >
              {saving ? 'Assigning...' : 'Set Primary Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dashboard Cards Modal */}
      <Dialog open={showDashboardModal} onOpenChange={setShowDashboardModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
              <LayoutGrid className="w-5 h-5" />
              Dashboard Cards - {selectedBranch?.display_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Toggle which dashboard cards are visible for this branch. Unchecked cards will be hidden from users in this branch.
            </p>
            <div className="space-y-2">
              {DASHBOARD_CARDS.map(card => (
                <div 
                  key={card.id}
                  data-testid={`dashboard-card-row-${card.id}`}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-sm">{card.name}</p>
                    <p className="text-xs text-gray-500">{card.description}</p>
                  </div>
                  <Checkbox
                    data-testid={`dashboard-card-toggle-${card.id}`}
                    checked={dashboardCards[card.id] !== false}
                    onCheckedChange={() => toggleDashboardCard(card.id)}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDashboardModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveDashboardSettings} 
              disabled={saving}
              style={{ backgroundColor: enconOrange }}
              className="text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchManager;
